import { Box, Checkbox, Stack, Text, Textarea, useToast } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { FormValues, IAttachmentSpecialCondition } from './attachmentList';
import { useQueryClient } from '@tanstack/react-query';
import { IGetRegistration } from '../../../../../services/hooks/Registration/useGetRegistration';
import AttachmentButtonFile from '../../../../../components/Mobile/attachmentButtonFile';
import { UseRegistration } from '../../../../../Store/useRegistration';
import { handleDeleteAttach } from '../../../../../utils/functions';

interface SpecialCondition {
  id: string;
  name: string;
  is_social_name?: boolean;
  has_attachment?: boolean;
  attachments?: IAttachmentSpecialCondition[];
}

interface Props {
  conditions: SpecialCondition[];
}

const SpecialConditionList = ({ conditions }: Props) => {
  const { registrationId } = UseRegistration();
  const { watch, register, formState } = useFormContext<FormValues>();
  const toast = useToast();

  const selecteds = watch('selectedSpecialCondition') || [];

  if (conditions.length === 0) {
    return <Text color="#E53E3E">Sem dados...</Text>;
  }

  const queryClient = useQueryClient();

  const specialConditionCached = queryClient.getQueryData<IGetRegistration>([
    'registration_data',
    registrationId ?? '',
  ])?.special_conditions;

  return (
    <Stack spacing={2} mt={2}>
      {conditions.map((condition, index) => {
        const isChecked = selecteds[index] || false;

        const listAttachment = specialConditionCached?.find((cached) => condition.id === cached.id);

        const onDeleteAttachment = async (id: string) => {
          await handleDeleteAttach(id, toast).then(() => {
            queryClient.setQueryData<IGetRegistration>(['registration_data', registrationId ?? ''], (data) => {
              if (!data) return data;

              return {
                ...data,
                special_conditions: data.special_conditions.map((sc) =>
                  sc.id === condition.id
                    ? {
                        ...sc,
                        attachments: sc.attachments?.filter((file) => file.id !== id) || [],
                      }
                    : sc,
                ),
              };
            });
          });
        };

        return (
          <Box
            key={condition.id}
            p={2}
            borderWidth="1px"
            borderRadius="md"
            bg={isChecked ? 'gray.50' : 'white'}
            transition="0.3s"
            shadow={isChecked ? 'md' : 'sm'}
          >
            <Checkbox
              {...register(`selectedSpecialCondition.${index}`)}
              isChecked={isChecked}
              value={condition.id}
              size="md"
              colorScheme="green"
            >
              {condition.name}
            </Checkbox>
            {isChecked && (
              <Stack mt={2} spacing={3}>
                {!condition.is_social_name && (
                  <>
                    <Textarea
                      placeholder="Descreva em detalhes a sua necessidade."
                      {...register(`observation.${index}`)}
                      bg="white"
                      h="130px"
                      resize="none"
                    />
                    {formState.errors.observation?.[index] && (
                      <Text mt={-2} color="#E53E3E">
                        {formState.errors.observation[index]?.message?.toString()}
                      </Text>
                    )}

                    {condition.has_attachment && (
                      <AttachmentButtonFile
                        index={index}
                        sentAttachments={listAttachment?.attachments.map((attach) => ({
                          id: attach.id,
                          name: attach.name,
                          url: attach.url,
                          created_at: attach.created_at_patern,
                        }))}
                        onDeleteAttachment={onDeleteAttachment}
                      />
                    )}
                  </>
                )}
              </Stack>
            )}
          </Box>
        );
      })}
      {formState.errors.selectedSpecialCondition && (
        <Text color="#E53E3E">{formState.errors.selectedSpecialCondition.root?.message?.toString()}</Text>
      )}
    </Stack>
  );
};

export default SpecialConditionList;
